// Drop-in error boundary. Wrap any drawer / panel / modal so an
// uncaught render error shows an inline message instead of unmounting
// the entire React tree (which is what causes the white-screen-of-
// nothing). Resets when the `resetKey` prop changes — handy for
// drawers keyed on customerId / orderNumber so re-opening clears the
// last error.
//
// Usage:
//   <ErrorBoundary resetKey={openId} label="Account drawer">
//     <AccountDrawer ... />
//   </ErrorBoundary>

import React from 'react';
import { reportError } from '../lib/reportError';
import { isDomMutationRace } from '../lib/domResilience';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // A reparented-node race (browser extension moving a node React owns) is
    // recoverable: the DOM shim absorbs it at source, but if one ever reaches
    // here, re-render ONCE instead of a dead "Try again" — React reconciles
    // cleanly afterwards. Reported as informational, never as a crash.
    if (isDomMutationRace(error) && !this._domRetried) {
      this._domRetried = true;
      try {
        reportError({ source: 'dom.mutation', message: `[${this.props.label || 'component'}] recovered from external DOM mutation: ${error?.message || error}` });
      } catch { /* never throw from a boundary */ }
      setTimeout(() => this.setState({ error: null }), 0);
      return;
    }
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.label || 'unknown'}]`, error, info?.componentStack);
    // Report to the server so drawer/modal crashes (Compose, Account, Cost
    // Sheet, …) land in app_errors and the daily digest — these used to be
    // console-only and invisible to the admin.
    try {
      reportError({
        source: 'react.boundary',
        message: `[${this.props.label || 'component'}] ${error?.message || String(error)}`,
        stack: (error?.stack || '') + (info?.componentStack ? '\n--- componentStack ---' + info.componentStack : ''),
      });
    } catch { /* reporting must never throw from a boundary */ }
  }

  componentDidUpdate(prevProps) {
    // Reset on caller-supplied key change so re-opening a drawer with
    // a different customer/order clears the last error.
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error?.message || String(this.state.error);
    return (
      <div role="alert" style={{
        margin: 24, padding: 16, borderRadius: 10,
        border: '1px solid #fdd', background: '#fef4f4',
        color: '#7a1f1f', fontFamily: 'system-ui, sans-serif',
        fontSize: 13, lineHeight: 1.5,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {this.props.label || 'Component'} crashed
        </div>
        <div style={{ marginBottom: 10, fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {msg}
        </div>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          style={{
            border: '1px solid #d4a3a3', background: '#fff', color: '#7a1f1f',
            padding: '5px 11px', borderRadius: 6, cursor: 'pointer',
            font: 'inherit',
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
